<script setup lang="ts">
import { MtdButton, MtdContainer, MtdSkeleton, MtdTypography } from '@paygine/mtd-uikit'
import { useEventListener } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'

import type { Steps } from '@/shared/constants'

import { subscriptionApi, useSubscription } from '@/features/subscription'
import { useConfig } from '@/shared/config'
import { SbpIcon } from '@/shared/icons'
import { createRedirectLink } from '@/shared/lib/utils'
import { VPayMethod, VSearchableList } from '@/shared/ui'

import VLoading from './VLoading.vue'

const { config } = useConfig()
const {
    addSbpSubscription,
    fetchPayMethods,
    isFetchingPayMethods,
    isLoading,
    payMethods,
    startCheckingStatus,
    stopCheckingStatus,
    subscriptionStatus,
} = useSubscription()

const emit = defineEmits<{
    goto: [Steps]
}>()

const needCheckStatus = ref(false)
const isBanksOpen = ref(false)
const nspkLink = ref<null | string>(null)

function openBank(bankUrl: number | string) {
    if (!bankUrl || !nspkLink.value) return

    needCheckStatus.value = true
    const redirectLink = createRedirectLink(nspkLink.value, String(bankUrl))

    if (redirectLink.length) {
        window.location.href = redirectLink
    }
}

async function subscribe() {
    if (!nspkLink.value) {
        nspkLink.value = await addSbpSubscription()
    }

    if (payMethods.value?.length && payMethods.value?.length === 1) {
        openBank(payMethods.value?.[0]?.sbp_url || '')
    } else {
        isBanksOpen.value = true
    }
}

useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible' && needCheckStatus.value) {
        startCheckingStatus()
    }
})

function statusHandler(status: null | subscriptionApi.SubscriptionStatus) {
    if (status !== 'PENDING') {
        stopCheckingStatus()
        needCheckStatus.value = false
    }

    switch (status) {
        case 'ACTIVE':
            config.subscriptionState = true
            emit('goto', 'notify')
            break
        case 'ERROR':
            emit('goto', 'error-status')
            break
        case 'INACTIVE':
            config.subscriptionState = false
            emit('goto', 'notify')
            break
        default:
            console.log(status)
    }
}

watch(subscriptionStatus, (status) => statusHandler(status))

onMounted(() => {
    fetchPayMethods()
})
</script>

<template>
    <div
        v-if="!needCheckStatus"
        class="select-pay-method"
    >
        <MtdTypography
            variant="header-6"
            class="default-layout__title"
            tag="h1"
            >Подтвердите способ оплаты
        </MtdTypography>

        <MtdTypography
            tag="p"
            variant="textbody-2"
            class="default-layout__subtitle"
            >Для подключения безакцептного списания при пользовании платными дорогами
        </MtdTypography>

        <MtdContainer
            v-if="isFetchingPayMethods"
            class="select-pay-method__skeleton"
        >
            <MtdSkeleton class="mt-32 select-pay-method__skeleton-item" />
            <MtdSkeleton class="mt-32 select-pay-method__skeleton-item" />
        </MtdContainer>

        <template v-if="payMethods && payMethods.length === 1">
            <VPayMethod
                class="mt-32"
                :can-edit="false"
                borderless
                :bank="{
                    bankName: payMethods[0]?.name || '',
                    id: 0,
                    logoUrl: payMethods[0]?.logo || '',
                    subtitle: 'СБП',
                }"
            />

            <MtdButton
                type="button"
                variant="main"
                class="mt-32"
                :is-loading="isLoading"
                @click="subscribe"
                >Подтвердить и оформить подписку</MtdButton
            >
        </template>

        <template v-else-if="payMethods && payMethods.length > 1">
            <div class="sbp mt-32">
                <SbpIcon class="sbp__icon" />
                <MtdTypography
                    variant="textbody-2-medium"
                    class="sbp__title"
                    tag="p"
                    >Система быстрых платежей</MtdTypography
                >
            </div>

            <MtdButton
                class="mt-12"
                :is-loading="isLoading"
                @click="subscribe"
                >Выбрать банк
            </MtdButton>

            <VSearchableList
                v-model="isBanksOpen"
                :empty="{
                    subtitle: 'Проверьте название банка',
                    title: 'Ничего не найдено',
                }"
                title="Выберите банк"
                :alphabetical="false"
                list-class="bank-list"
                list-item-class="bank-list__item"
                :items="
                    payMethods.map((bank) => ({
                        id: bank.sbp_url,
                        label: bank.name,
                        iconUrl: bank.logo,
                    }))
                "
                @select="({ id }) => openBank(id)"
            />
        </template>
    </div>

    <VLoading v-else />
</template>

<style lang="scss" src="./SelectPayMethod.scss"></style>
